import moment from 'moment';
import createLetterSeed from '../hubLetterSeed/hubLetterSeed';

(function init() {
  angular.module('app').controller('host.views.debugTools.triggerHubCreateCase.modal', [
    '$uibModalInstance',
    'abp.services.app.debugTool',
    function controller($uibModalInstance, debugToolSvc) {
      const vm = this;
      vm.saving = 0;
      vm.createdCase = null;
      // Two-page modal: 'form' collects the payload, 'result' replaces it with the
      // Hub response; Back returns to the form with the entered values intact.
      vm.view = 'form';

      vm.policyId = '';
      vm.providerId = '';
      vm.patientId = '';
      // The automatic flow always sends the Malaysian organisation constant
      // (HubIntegrationConstants.AssignerId); editable in case the Hub adds others [HM-11061].
      vm.providerManagingHubOrganizationId = 'hms-my';
      vm.benefitCategory = '';
      vm.visitType = '';
      vm.admissionType = '';
      vm.admissionDate = null;
      vm.admissionTimeUtc = moment();
      vm.dischargeDate = null;
      vm.dischargeTimeUtc = moment().add(1, 'day');
      vm.patientName = '';
      vm.registrationTimeUtc = null;
      vm.identifiers = [];
      vm.documents = [];

      // singleDatePicker mode binds the model as a bare moment (see the vendored
      // angular-daterangepicker wrapper), so models are read directly, not via .startDate.
      // Required timestamps default to now; the plugin only fires its callback when the
      // selection changes, so an empty field plus "pick today" would otherwise commit nothing.
      vm.datePickerOptions = {
        singleDatePicker: true,
        locale: {
          format: 'L',
          applyLabel: App.localize('Apply'),
          cancelLabel: App.localize('Cancel'),
        },
      };
      vm.dateTimePickerOptions = {
        singleDatePicker: true,
        timePicker: true,
        timePicker24Hour: true,
        locale: {
          format: 'L LT',
          applyLabel: App.localize('Apply'),
          cancelLabel: App.localize('Cancel'),
        },
      };
      vm.admissionDateOptions = vm.datePickerOptions;
      vm.dischargeDateOptions = vm.datePickerOptions;
      vm.admissionTimeOptions = vm.dateTimePickerOptions;
      vm.dischargeTimeOptions = vm.dateTimePickerOptions;
      vm.registrationTimeOptions = vm.dateTimePickerOptions;

      vm.benefitCategoryOptions = [
        'Hospitalization',
        'Maternity',
        'Gp',
        'Dental',
        'Optical',
        'OutpatientSpecialist',
        'MedicalScreening',
        'Pharmacy',
      ];
      vm.visitTypeOptions = ['Inpatient', 'Outpatient'];
      vm.admissionTypeOptions = ['Emergency', 'Elective', 'Urgent', 'Newborn', 'Other'];

      // Seeding from a letter [HM-11245]: how each payload field fills the form, and what the form
      // posts for it. The case Hub Sync Repair would create leaves admission and discharge times
      // empty, so a seeded form does not require them.
      const fromTime = (value) => (value ? moment(value) : null);
      const fromDate = (value) => (value ? moment(value, 'YYYY-MM-DD') : null);
      vm.seed = createLetterSeed({
        policyId: { fromSeed: (v) => v || '', toInput: (v) => v },
        providerId: { fromSeed: (v) => v || '', toInput: (v) => v },
        patientId: { fromSeed: (v) => v || '', toInput: (v) => v },
        providerManagingHubOrganizationId: { fromSeed: (v) => v || '', toInput: (v) => v },
        patientName: { fromSeed: (v) => v || '', toInput: (v) => v },
        benefitCategory: { fromSeed: (v) => v || '', toInput: (v) => v },
        visitType: { fromSeed: (v) => v || '', toInput: (v) => v },
        admissionType: { fromSeed: (v) => v || '', toInput: (v) => v || null },
        admissionDate: { fromSeed: fromDate, toInput: toDate },
        admissionTimeUtc: { fromSeed: fromTime, toInput: toUtc },
        dischargeDate: { fromSeed: fromDate, toInput: toDate },
        dischargeTimeUtc: { fromSeed: fromTime, toInput: toUtc },
        registrationTimeUtc: { fromSeed: fromTime, toInput: toUtc },
        identifiers: { fromSeed: (v) => v || [], toInput: (v) => v },
        documents: { fromSeed: (v) => v || [], toInput: (v) => v },
      });
      vm.loadSeed = () =>
        vm.seed.load(vm, (input) => debugToolSvc.getHubCreateCaseSeed(input));
      vm.resetSeed = () => vm.seed.reset(vm);

      vm.cancel = cancel;
      vm.execute = execute;
      vm.back = back;
      vm.addIdentifier = addIdentifier;
      vm.removeIdentifier = removeIdentifier;
      vm.addDocument = addDocument;
      vm.removeDocument = removeDocument;

      function cancel() {
        $uibModalInstance.dismiss();
      }

      function back() {
        vm.view = 'form';
      }

      function addIdentifier() {
        vm.identifiers.push({ assignerId: '', type: '', value: '' });
      }

      function removeIdentifier(index) {
        vm.identifiers.splice(index, 1);
      }

      function addDocument() {
        vm.documents.push({ assignerId: '', type: '', value: '', title: '', contentType: '' });
      }

      function removeDocument(index) {
        vm.documents.splice(index, 1);
      }

      // Whole-second UTC, matching the Hub's own examples — moment's toISOString() adds
      // milliseconds, which the Hub's timestamps never carry.
      function toUtc(value) {
        return value ? moment(value).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      }

      function toDate(value) {
        return value ? moment(value).format('YYYY-MM-DD') : null;
      }

      function displayUtc(value) {
        return value ? moment.utc(value).format('YYYY-MM-DD HH:mm:ss [UTC]') : null;
      }

      // Rows with no value are dropped rather than shown blank.
      function addRow(rows, label, value) {
        if (value !== null && value !== undefined && value !== '') rows.push({ label, value });
      }

      function buildSummary(result) {
        const caseRows = [];
        addRow(caseRows, 'Case number', result.caseNumber);
        addRow(caseRows, 'Hub case ID', result.id);
        addRow(caseRows, 'Status', result.status);
        addRow(caseRows, 'Created', displayUtc(result.createdUtc));
        addRow(caseRows, 'Registered', displayUtc(result.registeredTimeUtc));
        addRow(caseRows, 'Created by', result.creatorId);

        const patientRows = [];
        addRow(patientRows, 'Patient name', result.patientName);
        addRow(patientRows, 'Hub patient ID', result.patientId);

        const coverageRows = [];
        addRow(coverageRows, 'Hub policy ID', result.policyId);
        addRow(coverageRows, 'Benefit category', result.benefitCategory);
        addRow(coverageRows, 'Visit type', result.visitType);
        addRow(coverageRows, 'Admission type', result.admissionType);
        addRow(coverageRows, 'Policy managing organisation', result.policyManagingHubOrganizationId);
        addRow(coverageRows, 'Provider ID', result.providerId);
        addRow(coverageRows, 'Provider managing organisation', result.providerManagingHubOrganizationId);

        const stayRows = [];
        addRow(stayRows, 'Admission date', result.admissionDate);
        addRow(stayRows, 'Admission time', displayUtc(result.admissionTimeUtc));
        addRow(stayRows, 'Discharge date', result.dischargeDate);
        addRow(stayRows, 'Discharge time', displayUtc(result.dischargeTimeUtc));
        addRow(stayRows, 'Identifiers', (result.identifiers || []).length || null);
        addRow(stayRows, 'Documents', (result.documents || []).length || null);

        return {
          caseNumber: result.caseNumber,
          status: result.status,
          groups: [
            { title: 'Case', rows: caseRows },
            { title: 'Patient', rows: patientRows },
            { title: 'Coverage', rows: coverageRows },
            { title: 'Stay', rows: stayRows },
          ].filter((group) => group.rows.length),
        };
      }

      function execute() {
        if (!App.isFormValid(vm.triggerHubCreateCaseForm)) return;

        // Every field is sent explicitly — unset dates as null, empty collections as [] — so the
        // request is identical in shape to the Hub's documented payload rather than relying on
        // the server to reinstate what the browser left out [HM-11061]. Seeded fields left
        // untouched go back exactly as seeded [HM-11245].
        const input = vm.seed.toInput(vm, {
          policyId: vm.policyId,
          providerId: vm.providerId,
          patientId: vm.patientId,
          providerManagingHubOrganizationId: vm.providerManagingHubOrganizationId,
          benefitCategory: vm.benefitCategory,
          visitType: vm.visitType,
          patientName: vm.patientName,
          identifiers: vm.identifiers,
          documents: vm.documents,
          admissionDate: toDate(vm.admissionDate),
          admissionTimeUtc: toUtc(vm.admissionTimeUtc),
          dischargeDate: toDate(vm.dischargeDate),
          dischargeTimeUtc: toUtc(vm.dischargeTimeUtc),
          registrationTimeUtc: toUtc(vm.registrationTimeUtc),
          admissionType: vm.admissionType || null,
        });
        // Only sent when chosen: the Hub's admissionType enum rejects a literal null.
        if (!input.admissionType) delete input.admissionType;

        vm.saving += 1;
        vm.createdCase = null;
        debugToolSvc
          .triggerHubCreateCase(input)
          .success((result) => {
            vm.createdCase = buildSummary(result);
            vm.view = 'result';
            abp.notify.success(`Case ${result.caseNumber} created on the Hub.`);
          })
          .finally(() => {
            vm.saving -= 1;
          });
      }
    },
  ]);
})();
